<template>
  <section class="position-relative">
    <div class="container-fluid px-0">
      <div
        class="row bg-backdrop d-none d-lg-block gx-0 mt-space-xxl"
        :class="{ 'spa-lazy': bgImageLazy }"
        :style="backdropStyle"
        :data-background-image="dataBgImage">
        <div class="parallax-bg"></div>
        <div :class="desktopColumnClass" style="z-index: 1">
          <card-info-horizontal
            class="card-info"
            :display-cta="displayCta"
            :cta-text="ctaText"
            :cta-text-size="ctaTextSize"
            :cta-link="ctaLink"
            :bg-image-source="bgImageSource"
            :title="title"
            :title-level="titleLevel"
            :title-size="titleSize"
            :title-variant="titleVariant"
            :border-variant="borderVariant"
            :display-logo="displayLogo"
            :logo-source="logoSource"
            :logo-alt="logoAlt"
            :display-highlight="displayHighlight"
            :highlight-color="highlightColor"
            :highlight-width="highlightWidth"
            :display-label="displayLabel"
            :label-bg="labelBg"
            :label-text="labelText"
            :label-text-size="labelTextSize"
            :label-text-variant="labelTextVariant"
            :cta-as-button="ctaAsButton"
            :cta-button-variant="ctaButtonVariant"
            :cta-text-variant="ctaTextVariant"
            :cta-text-weight="ctaTextWeight"
            :cta-link-target="ctaLinkTarget"
            @ctaClick="handleCtaClick($event)">
            <slot />
          </card-info-horizontal>
        </div>
      </div>
    </div>

    <div class="row d-block d-lg-none mx-space-xs mx-md-space-sm gx-0">
      <div class="col gx-0">
        <card-info-horizontal
          :display-cta="displayCta"
          :cta-text="ctaText"
          :cta-text-size="ctaTextSize"
          :cta-link="ctaLink"
          :bg-image-source="bgImageSource"
          :title="title"
          :title-level="titleLevel"
          :title-size="titleSize"
          :title-variant="titleVariant"
          :border-variant="borderVariant"
          :display-logo="displayLogo"
          :logo-source="logoSource"
          :logo-alt="logoAlt"
          :display-highlight="displayHighlight"
          :highlight-color="highlightColor"
          :highlight-width="highlightWidth"
          :display-label="displayLabel"
          :label-bg="labelBg"
          :label-text="labelText"
          :label-text-size="labelTextSize"
          :label-text-variant="labelTextVariant"
          :cta-as-button="ctaAsButton"
          :cta-button-variant="ctaButtonVariant"
          :cta-text-variant="ctaTextVariant"
          :cta-text-weight="ctaTextWeight"
          :cta-link-target="ctaLinkTarget"
          @ctaClick="handleCtaClick($event)">
          <slot />
        </card-info-horizontal>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { CardInfoHorizontal } from "@rds-vue-ui/card-info-horizontal";
import lozad from "lozad";
import { computed, onMounted, type StyleValue } from "vue";

interface Props {
  bgImageSource: string;
  bgImageLazy?: boolean;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: string;
  titleVariant?: string;
  displayLogo?: boolean;
  logoSource?: string;
  logoAlt?: string;
  displayHighlight?: boolean;
  highlightColor?: string;
  highlightWidth?: number;
  displayLabel?: boolean;
  labelBg?: string;
  labelText?: string;
  labelTextSize?: string;
  labelTextVariant?: string;
  borderVariant?: string;
  displayCta?: boolean;
  ctaText?: string;
  ctaTextSize?: string;
  ctaTextVariant?: string;
  ctaTextWeight?: string;
  ctaAsButton?: boolean;
  ctaButtonVariant?: string;
  ctaLink?: string;
  ctaLinkTarget?: "_self" | "_blank" | "_parent" | "_top";
  lgColumns?: string;
  offsetLgColumns?: string;
  backdropHeight?: string;
}

const props = withDefaults(defineProps<Props>(), {
  bgImageLazy: true,
  titleLevel: "h3",
  titleSize: "small",
  titleVariant: "dark-3",
  displayLogo: false,
  logoSource: "https://via.placeholder.com/200x36?text=logoSource",
  logoAlt: "cih logo alt",
  displayHighlight: true,
  highlightColor: "var( --rds-secondary, #ffc627)",
  highlightWidth: 5,
  displayLabel: false,
  labelBg: "secondary",
  labelText: "lorem ipsum",
  labelTextSize: "xs",
  labelTextVariant: "dark-3",
  borderVariant: "light-4",
  displayCta: true,
  ctaText: "Read more",
  ctaTextSize: "large",
  ctaTextVariant: "primary",
  ctaTextWeight: "normal",
  ctaAsButton: false,
  ctaButtonVariant: "primary",
  ctaLink: "https://asuonline.asu.edu/",
  ctaLinkTarget: "_blank",
  lgColumns: "8",
  offsetLgColumns: "2",
  backdropHeight: "492",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the cta is clicked, if present.
   */
  (e: "ctaClick", event: Event): void;
}>();

const desktopColumnClass = computed(() => {
  return `offset-lg-${props.offsetLgColumns} col-lg-${props.lgColumns} position-relative`;
});
const backdropStyle = computed(() => {
  const style: StyleValue = {};
  style["--bd-height"] = `${props.backdropHeight}px`;

  if (!props.bgImageLazy) {
    style["--section-bg-image"] = `url('${props.bgImageSource}')`;
  }

  return style;
});

const dataBgImage = computed(() => {
  return props.bgImageLazy ? props.bgImageSource : null;
});

const handleCtaClick = (event: Event) => {
  emits("ctaClick", event);
};

onMounted(() => {
  if (props.bgImageLazy) {
    const observer = lozad(".spa-lazy", {
      loaded: function (el: HTMLElement) {
        el.style.setProperty(
          "--section-bg-image",
          `url("${el.getAttribute("data-background-image")}")`
        );
      },
    });
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
.bg-backdrop {
  height: var(--bd-height, 492px);
  position: relative;
  background: rgba(0, 0, 0, 0.6);
  background-image: none !important;
}

.parallax-bg {
  height: var(--bd-height, 492px);
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  webkit-clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  -webkit-clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
}

.parallax-bg:after {
  content: "";
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: fixed;
  background-position: center 5%;
  background-size: cover;
  background-repeat: no-repeat;
  will-change: transform;
  box-shadow: inset 0 0 0 2000px rgba(31, 30, 30, 0.82);
  background-image: var(--section-bg-image);
}

.card-info {
  top: -92px;
  padding: 0;
  /* added to fix clip-mask z-index issue */
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
}
</style>
